import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyOrders, getConsent, updateConsent, logoutShopCustomer } from "./api";
import ShopNavBar from "./components/ShopNavBar";
import EmptyState from "./components/EmptyState";
import RenderTranslatedText from "../../components/RenderTranslatedText";
import { analytics } from "../../analytics";

const STATUS_COLORS = {
  PAID: "text-green-700 bg-green-50",
  CONFIRMED: "text-green-700 bg-green-50",
  PENDING: "text-amber-700 bg-amber-50",
};

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${
        checked ? "otaGradientButton" : "bg-gray-200"
      } disabled:opacity-60`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
          checked ? "translate-x-[18px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function OrdersSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 2 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 p-3 rounded-xl border border-gray-100"
        >
          <div className="w-10 h-10 rounded-lg bg-gray-100 animate-pulse shrink-0" />
          <div className="flex-1 flex flex-col gap-2">
            <div className="h-3 w-2/3 bg-gray-100 rounded animate-pulse" />
            <div className="h-2.5 w-1/3 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const nickname = localStorage.getItem("shopUserNickname") || "there";
  const email = localStorage.getItem("shopUserEmail") || "";

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [consent, setConsent] = useState(null);
  const [savingConsent, setSavingConsent] = useState(false);
  const [consentError, setConsentError] = useState(null);

  useEffect(() => {
    getMyOrders({ limit: 20 })
      .then((res) => setOrders(res.data.orders || []))
      .catch(() => {})
      .finally(() => setLoadingOrders(false));

    getConsent()
      .then((res) => setConsent(res.data))
      .catch(() => {});
  }, []);

  const toggleConsent = async (field, value) => {
    if (!consent) return;
    const prev = consent;
    setConsentError(null);
    setConsent({ ...consent, [field]: value });
    setSavingConsent(true);
    try {
      await updateConsent({ [field]: value });
      if (field === "analyticsTrackingAccepted") analytics.setConsent(value);
    } catch (err) {
      setConsent(prev);
      setConsentError(err.message || "Couldn't save that preference.");
    } finally {
      setSavingConsent(false);
    }
  };

  const needsRequiredConsent =
    consent && (!consent.eulaAccepted || !consent.dataProcessingAccepted);

  const handleLogout = () => {
    logoutShopCustomer();
    navigate("/shop/login", { replace: true });
  };

  return (
    <div className="min-h-screen pb-10">
      <ShopNavBar
        title={<RenderTranslatedText i8nKey="shop_profile_title" />}
        showProfile={false}
      />

      <div className="px-4 pt-5 flex flex-col gap-6">
        <div className="flex items-center gap-3.5 p-4 rounded-2xl bg-gradient-to-br from-[#07B9CE0d] via-[#3969E70d] to-[#7D2AE70d] border border-gray-100">
          <div className="w-14 h-14 rounded-full otaGradientButton flex items-center justify-center text-white font-bold text-xl shrink-0">
            {nickname[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-bold otaDark truncate">{nickname}</p>
            {email && <p className="text-xs otaLight truncate">{email}</p>}
          </div>
        </div>

        <button
          onClick={() => navigate("/shop/shops")}
          className="flex items-center justify-between p-3.5 rounded-xl border border-gray-100 cardShadow active:scale-[0.98] transition-transform"
        >
          <span className="flex items-center gap-2.5 text-sm font-semibold otaDark">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 7h16l-1.5 11.2a2 2 0 01-2 1.8H7.5a2 2 0 01-2-1.8L4 7z"
                stroke="#3969E7"
                strokeWidth="1.6"
              />
              <path d="M8 7V5a4 4 0 018 0v2" stroke="#3969E7" strokeWidth="1.6" />
            </svg>
            Browse Shops
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 18l6-6-6-6"
              stroke="#9CA3AF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div>
          <p className="text-sm font-semibold otaDark mb-2">
            <RenderTranslatedText i8nKey="shop_profile_orders_title" />
          </p>
          {loadingOrders && <OrdersSkeleton />}
          {!loadingOrders && orders.length === 0 && (
            <EmptyState
              title="No orders yet"
              subtitle="Your purchases from shops will show up here."
            />
          )}
          {!loadingOrders && orders.length > 0 && (
            <div className="flex flex-col gap-2">
              {orders.map((order) => (
                <button
                  key={order.id}
                  onClick={() => navigate(`/shop/profile/orders/${order.id}`)}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 cardShadow text-left active:scale-[0.98] transition-transform"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                    {order.shopFront?.logoUrl && (
                      <img
                        src={order.shopFront.logoUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium otaDark truncate">
                      {order.shopFront?.name || "Shop"}
                    </p>
                    <p className="text-xs otaLight">
                      {order.itemCount} item{order.itemCount === 1 ? "" : "s"} ·{" "}
                      {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold otaDark">
                      ₹{order.totalAmount.toFixed(0)}
                    </p>
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        STATUS_COLORS[order.paymentStatus] ||
                        "text-gray-600 bg-gray-100"
                      }`}
                    >
                      {order.paymentStatus}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {consent && (
          <div>
            <p className="text-sm font-semibold otaDark mb-2">Preferences</p>

            {needsRequiredConsent ? (
              <button
                onClick={() =>
                  navigate("/shop/onboarding", {
                    state: { from: "/shop/profile" },
                  })
                }
                className="w-full text-left p-3.5 rounded-xl border border-amber-200 bg-amber-50 active:scale-[0.98] transition-transform"
              >
                <p className="text-sm font-semibold text-amber-800">
                  Finish setting up your account
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Accept the required terms to manage notification &amp;
                  privacy preferences.
                </p>
              </button>
            ) : (
              <>
                {consentError && (
                  <p className="text-xs text-red-600 mb-2">{consentError}</p>
                )}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                    <span className="text-sm otaDark">Anonymous analytics</span>
                    <Toggle
                      checked={consent.analyticsTrackingAccepted}
                      onChange={(v) =>
                        toggleConsent("analyticsTrackingAccepted", v)
                      }
                      disabled={savingConsent}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                    <span className="text-sm otaDark">Push notifications</span>
                    <Toggle
                      checked={consent.pushNotificationsAccepted}
                      onChange={(v) =>
                        toggleConsent("pushNotificationsAccepted", v)
                      }
                      disabled={savingConsent}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                    <span className="text-sm otaDark">Offers & promotions</span>
                    <Toggle
                      checked={consent.marketingCommsAccepted}
                      onChange={(v) =>
                        toggleConsent("marketingCommsAccepted", v)
                      }
                      disabled={savingConsent}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <button
          onClick={handleLogout}
          className="text-red-500 text-sm text-center rounded-xl border-2 border-red-500 w-full p-2.5 bg-white font-medium active:bg-red-50 transition-colors"
        >
          <RenderTranslatedText i8nKey="shop_profile_logout" />
        </button>
      </div>
    </div>
  );
}
